import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  DollarSign, 
  Calendar, 
  HelpCircle, 
  TrendingUp,
  FileText
} from 'lucide-react'
import Card from '../components/shared/Card'
import Button from '../components/shared/Button'
import ButtonGroup from '../components/shared/ButtonGroup'
import { useData, getCompanyMetrics, transformToTimeline, groupByQuarter } from '../hooks/useApi'
import { getStatusColor, transformUpdates } from '../utils/dashboardTransformers'
import PortfolioUpdates from '../components/dashboard/PortfolioUpdates'
import InvestmentTimeline from '../components/dashboard/InvestmentTimeline'
import AccountOverview from '../components/dashboard/AccountOverview'
import DocumentItem from '../components/shared/DocumentItem'
import { APP_CONFIG } from '../utils/constants'

/**
 * Portfolio Company Detail Page
 * Displays comprehensive information about a specific portfolio company
 * Shows company details, investment history, and related information
 * Fetches data from /api/startups/{startupId}, /api/investment/, /api/startups/{startupId}/updates/, and /api/follow-ons/
 */
const PortfolioCompanyDetailPage = () => {
  const { startupId } = useParams()
  const [activeTab, setActiveTab] = useState('Investments')

  // Single hook with parallel fetching
  const { data, loading, error, refetch } = useData('company', startupId)
  
  // Destructure the parallel-fetched data
  const { company: companyData, metrics: metricsData, investments: investmentsData, updates: updatesData, followOns: followOnsData } = data || {}

  // Tab options
  const tabOptions = ['Investments', 'Company Updates', 'Follow-On Deals']

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  // Handle retry on error - single refetch call
  const handleRetry = () => {
    refetch()
  }

  // Transform data for display
  const transformedInvestments = transformToTimeline(investmentsData)
  const timelineGroups = groupByQuarter(transformedInvestments)
  
  // Transform company updates - API now provides companyName and startupId directly
  const transformedUpdates = updatesData ? transformUpdates(updatesData) : []

  // Get follow-on deals for current company (API returns only relevant follow-ons)
  const followOnDeals = followOnsData?.results || []

  // Build metrics using centralized transformer for UI continuity
  const companyAccountOverview = getCompanyMetrics(metricsData, loading, [DollarSign, Calendar, TrendingUp])

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'Investments':
        return (
          <InvestmentTimeline 
            timelineData={transformedInvestments}
            timelineGroups={timelineGroups}
            loading={loading}
            isOnDetailPage={true}
          />
        )

      case 'Company Updates':
        return (
          <PortfolioUpdates 
            latestUpdates={transformedUpdates} 
            loading={loading}
            pageSize={5}
          />
        )

      case 'Follow-On Deals':
        return (
          <Card
            shadow="medium"
            title="Follow-On Deals"
            subtitle={`Available follow-on investment opportunities in ${companyData?.name || 'this company'}`}
          >
            <div className="divide-y divide-gray-200">
              {followOnDeals.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No follow-on opportunities available for this company</p>
                </div>
              ) : (
                followOnDeals.map((deal) => (
                  <div key={deal.id} className="p-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                      <div className="flex-1 md:pr-3">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900 text-base">
                            {deal.companyName || 'Unknown Company'}
                          </h3>
                          {deal.status && (
                          <span className={`px-2 py-1 ${getStatusColor(deal.status)} text-white text-xs rounded-full`}>
                            {deal.status}
                          </span>
                          )}
                        </div>
                        
                        <div className="space-y-1 mb-3">
                          <p className="text-sm text-gray-600">
                            Deal Size: <span className="font-medium text-gray-900">{deal.dealSize || '—'}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            Contact: <span className="font-medium text-gray-900">{deal.primaryContactName || 'Contact TNT'}</span>
                          </p>
                          {deal.primaryContactEmail && (
                            <p className="text-sm text-gray-600">
                              Email: <a href={`mailto:${deal.primaryContactEmail}`} className="text-blue-600 hover:text-blue-800 font-medium">
                                {deal.primaryContactEmail}
                              </a>
                            </p>
                          )}
                        </div>

                        {deal.description && (
                          <div className="mb-3">
                            <p className="text-sm text-gray-600 leading-relaxed">
                              {deal.description}
                            </p>
                          </div>
                        )}

                        {deal.documents && deal.documents.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm text-gray-600 mb-1">Documents:</p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {deal.documents.map((document) => (
                                <DocumentItem 
                                  key={document.id}
                                  document={document}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="md:text-right md:ml-4 mt-3 md:mt-0">
                        <Button
                          variant="outline"
                          size="small"
                          className="text-sm"
                          href={deal.interestFormUrl || (`mailto:${deal.primaryContactEmail || APP_CONFIG.SUPPORT_EMAIL}?subject=Follow-on Opportunity: ${deal.companyName}`)}
                          target="_blank"
                        >
                          {deal.interestFormUrl ? 'Indicate Interest' : 'Contact TNT'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        )

      default:
        return null
    }
  }

  // Single loading and error states - much simpler!
  const isLoading = loading
  const hasError = error


  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* Back Navigation */}
      <div className="mb-6">
        <Link 
          to="/portfolio" 
          className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>

      {/* Loading State */}
      {isLoading && (
        <Card shadow="medium" padding="large">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading company details...</p>
          </div>
        </Card>
      )}

      {/* Error State */}
      {hasError && (
        <Card shadow="medium" padding="large">
          <div className="text-center py-8">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <p className="text-gray-600 text-lg mb-4">Failed to load company data</p>
            <p className="text-gray-500 mb-6">Please try again or contact support if the problem persists.</p>
            <Button onClick={handleRetry} variant="primary">
              Try Again
            </Button>
          </div>
        </Card>
      )}

      {/* Main Content - Only show when not loading and no error */}
      {!isLoading && !hasError && companyData && (
        <>
          {/* Company Header */}
          <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
              {/* Company Logo - Square format */}
              <div className="flex-shrink-0">
                {companyData.logo ? (
                  <img
                    src={companyData.logo}
                    alt={`${companyData.name} logo`}
                    className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover border border-gray-200 shadow-sm"
                    onError={(e) => {
                      // Fallback to placeholder if image fails to load
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                ) : null}
                {/* Fallback placeholder when no logo or image fails to load */}
                <div 
                  className={`w-20 h-20 md:w-24 md:h-24 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center ${companyData.logo ? 'hidden' : 'flex'}`}
                >
                  <div className="text-gray-400 text-2xl font-bold">
                    {companyData.name ? companyData.name.charAt(0).toUpperCase() : '?'}
                  </div>
                </div>
              </div>
              
              {/* Company Information */}
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {companyData.name}
                </h1>
                <p className="text-gray-600 text-lg">
                  {companyData.hqProvince || ''}
                </p>
              </div>
            </div>
          </div>

          {/* Account Overview - replace card grid for simplification and consistency */}
          <AccountOverview 
            accountOverview={companyAccountOverview}
            loading={loading}
            style="grid"
            direction="row"
            className="mb-0 rounded-b-none" 
            cardBevelStyle="rounded-b-none"
          />

          {/* Company Information Section */}
          <Card 
            shadow="medium"
            title="Company Information"
            className="mb-6 rounded-t-none border-t-0"
          >
            <div className="">
              {/* <div className="mb-4">
                <HelpCircle className="w-16 h-16 text-gray-400 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                What information would investors like to see?
              </h3>
              <p className="text-gray-600 mb-3">
                We're considering what company information would be most valuable to display here. 
                Your input would help us prioritize the right features.
              </p>
              <p className="text-gray-600">
                Please share your thoughts on what company details, metrics, or insights you'd find most useful.
              </p> */}

              <span className="text-gray-700 font-bold text-xxs uppercase">Description</span>
              <p className="text-gray-600 text-sm mb-2" style={{ marginTop: '-3px' }}>
                {companyData.description || 'No description available'}
              </p>

                {companyData.industry && (
                  <>
                    <span className="text-gray-700 font-bold text-xxs uppercase">Industry</span>
                    <p className="text-gray-600 text-sm mb-2" style={{ marginTop: '-3px' }}>
                        {companyData.industry}
                    </p>
                  </>
                )}
                {companyData.website && (
                <>
                <span className="text-gray-700 font-bold text-xxs uppercase">Website</span>
                  <p className="text-blue-600 text-sm mb-2" style={{ marginTop: '-3px' }}>
                    <a href={companyData.website} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {companyData.website}
                    </a>
                  </p>
                  </>
                )}
                {companyData.companyLinkedIn && (
                <>
                <span className="text-gray-700 font-bold text-xxs uppercase">LinkedIn</span>
                  <p className="text-blue-600 text-sm mb-2" style={{ marginTop: '-3px' }}>
                    <a href={companyData.companyLinkedIn} target="_blank" rel="noopener noreferrer" className="hover:underline">
                      {companyData.companyLinkedIn}
                    </a>
                  </p>
                  </>
                )}
              {/* <div className="space-y-1 mb-3">
                          <p className="text-sm text-gray-600">
                            Deal Size: <span className="font-medium text-gray-900">{deal.dealSize || '—'}</span>
                          </p>
                          <p className="text-sm text-gray-600">
                            Contact: <span className="font-medium text-gray-900">{deal.primaryContactName || 'Contact TNT'}</span>
                          </p>
                          {deal.primaryContactEmail && (
                            <p className="text-sm text-gray-600">
                              Email: <a href={`mailto:${deal.primaryContactEmail}`} className="text-blue-600 hover:text-blue-800 font-medium">
                                {deal.primaryContactEmail}
                              </a>
                            </p>
                          )}
                        </div> */}
              
            </div>
          </Card>

          {/* Navigation Tabs */}
          <div className="mb-6 text-center">
            <ButtonGroup
              options={tabOptions}
              value={activeTab}
              onChange={handleTabChange}
              size="medium"
              variant="default"
            />
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </>
      )}
    </div>
  )
}

export default PortfolioCompanyDetailPage
